import { EpicIcons } from "@constellation-academy/epic-ui";
import * as Sentry from "@sentry/react";
import { memo, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import type { resetLearnProgressButton_LearnOpportunityV2Fragment$key } from "@relay/resetLearnProgressButton_LearnOpportunityV2Fragment.graphql";
import type { resetLearnProgressButton_ResetLearnProgressMutation } from "@relay/resetLearnProgressButton_ResetLearnProgressMutation.graphql";
import {
	LEARN_OPPORTUNITY_V2_FRAGMENT,
	RESET_LEARN_PROGRESS_MUTATION,
} from "./reset-learn-progress-button.graphql";
import type { ResetLearnProgressButtonProps } from "./reset-learn-progress-button.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { ConfirmDialog } from "../../../../components/confirm-dialog";

const ResetLearnProgressButtonComponent = ({
	learnOpportunityV2FragmentRef,
	userId,
	onCompleted,
	onError,
}: ResetLearnProgressButtonProps) => {
	const { t } = useTranslation("common");
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const rootNode = useFragment<resetLearnProgressButton_LearnOpportunityV2Fragment$key>(
		LEARN_OPPORTUNITY_V2_FRAGMENT,
		learnOpportunityV2FragmentRef ?? null,
	);

	const [resetLearnProgress, isResettingLearnProgress] =
		useMutation<resetLearnProgressButton_ResetLearnProgressMutation>(
			RESET_LEARN_PROGRESS_MUTATION,
		);

	const handleResetLearnProgressOnClick = () => {
		setShowConfirmDialog(true);
	};

	const handleResetLearnProgressOnCancel = () => {
		setShowConfirmDialog(false);
	};

	const handleResetLearnProgressOnConfirm = () => {
		if (!rootNode) {
			setShowConfirmDialog(false);
			toast.error(t("userEdit.resetLearnProgressRootNotFound"));
			Sentry.captureException(
				new Error(
					"resetLearnProgressButton_ResetLearnProgressMutation: RootNode not found",
				),
				{
					extra: {
						userId,
						rootNode,
					},
				},
			);
			return;
		}

		resetLearnProgress({
			variables: {
				input: { rootId: rootNode.id, userId },
			},
			onCompleted: () => {
				setShowConfirmDialog(false);
				toast.success(t("userEdit.resetLearnProgressSuccess"));
				onCompleted?.();
				window.location.reload();
			},
			onError: () => {
				setShowConfirmDialog(false);
				toast.error(t("userEdit.resetLearnProgressError"));
				onError?.();
			},
		});
	};

	return (
		<>
			<Button
				label={t("userEdit.resetLearnProgress")}
				icon={EpicIcons.BACKWARD}
				variant={ButtonVariant.Error}
				onClick={handleResetLearnProgressOnClick}
				disabled={isResettingLearnProgress}
				loading={isResettingLearnProgress}
			/>
			<ConfirmDialog
				title={t("userEdit.resetLearnProgress")}
				content={
					<div>
						{t("userEdit.resetLearnProgressConfirm", {
							name:
								rootNode?.structureDefinition.title ?? t("userEdit.unknownCourse"),
						})}
						<br />
						{t("userEdit.resetLearnProgressDetail")}
						<br />
						<strong>{t("userEdit.resetLearnProgressIrreversible")}</strong>
					</div>
				}
				confirmButtonText={t("userEdit.resetLearnProgressConfirmButton")}
				onConfirm={handleResetLearnProgressOnConfirm}
				onCancel={handleResetLearnProgressOnCancel}
				showDialog={showConfirmDialog}
			/>
		</>
	);
};

export const ResetLearnProgressButton = memo(ResetLearnProgressButtonComponent);
